import React, { useState, useEffect } from "react";
import LockIcon from "./lock.png";
import ScaleIcon from "./scale.png";
import GearIcon from "./gear.png";
import FolderIcon from "./folder.png";
import ChatIcon from "./chat.png";

const ArchitectureSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const platformComponents = [
    {
      icon: <img src={LockIcon} alt="Air-Gapped Security" className="icon" />,
      heading: "Air-Gapped Security: Offline Equals Unhackable",
      subheading: "Achieve unparalleled data protection with complete network isolation, rendering your AI immune to external cyber threats.",
      points: [
        "**Complete Isolation:** Ensure full separation from external threats, eliminating remote attack vectors.",
        "**Verified Updates:** System updates are delivered exclusively via physically verified media, preventing supply chain attacks.",
        "**Zero Vulnerabilities:** Mitigate supply chain vulnerabilities by controlling every aspect of software and hardware updates.",
        "**Ransomware Immunity:** Our air-gapped architecture makes your critical data literally unreachable by ransomware and other online attacks.",
      ],
    },
    {
      icon: <img src={FolderIcon} alt="Compliance Built-In" className="icon" />,
      heading: "Compliance Built-In: Audit-Ready Every Second",
      subheading: "Maintain continuous regulatory compliance with automated, immutable audit trails and granular access controls.",
      points: [
        "**Comprehensive Logging:** Every AI query and data interaction is meticulously logged with full contextual information.",
        "**Immutable Audit Trail:** Leverage blockchain-verified audit trails that are tamper-proof and irrefutable for regulatory scrutiny.",
        "**Role-Based Access:** Enforce strict role-based access control (RBAC) to ensure only authorized personnel can interact with sensitive AI functions.",
        "**Automated Data Retention:** Implement and automate data retention policies, simplifying compliance with legal and industry standards.",
      ],
    },
    {
      icon: <img src={ScaleIcon} alt="Performance Guaranteed" className="icon" />,
      heading: "Performance : Faster Than Cloud, More Reliable",
      subheading: "Experience superior AI performance with ultra-low latency and exceptional uptime, unburdened by internet dependencies or cloud outages.",
      points: [
        "**Sub-100ms Response:** Achieve blazing-fast AI response times of less than 100 milliseconds within your local network.",
        "**99.99% Uptime:** Benefit from a robust infrastructure delivering 99.99% uptime, far surpassing typical cloud service level agreements.",
        "**Local Processing Power:** Process millions of documents and complex queries locally, eliminating reliance on external bandwidth limitations.",
        "**Scalability Without Limits:** Scale your AI capabilities without incurring additional bandwidth costs or being throttled by internet speeds.",
      ],
    },
    {
      icon: <img src={GearIcon} alt="Total Cost Ownership" className="icon" />,
      heading: "Predictable Pricing: Total Cost of Ownership Advantage",
      subheading: "Enjoy transparent, fixed-cost AI solutions that eliminate unpredictable cloud expenses and deliver clear return on investment.",
      points: [
        "**Fixed Cost, Unlimited Queries:** Benefit from a predictable, fixed annual cost, allowing unlimited queries and users without hidden fees.",
        "**No Per-Token Shocks:** Say goodbye to unpredictable per-token billing, ensuring budget stability and cost control.",
        "**Reduced Insurance Premiums:** Lower your breach insurance premiums by significantly reducing data exposure and demonstrating superior security.",
        "**Eliminated Overhead:** Streamline operations and eliminate the complex overhead associated with managing cloud compliance and security.",
      ],
    },
    {
      icon: <img src={ChatIcon} alt="Canadian Support" className="icon" />,
      heading: "Canadian Support: 24/7, Local, and Secure",
      subheading: "Access expert, bilingual support from security-cleared Canadian personnel, ensuring timely assistance and complete data confidentiality.",
      points: [
        "**Bilingual Assistance:** Receive comprehensive support in both English and French, catering to Canada's official languages.",
        "**On-Site Availability:** Benefit from optional on-site assistance for critical issues, providing rapid, hands-on resolution.",
        "**Security-Cleared Staff:** Interact with security-cleared personnel who understand the nuances of Canadian privacy and security requirements.",
        "**Never Outsourced:** Our support is entirely in-house and never outsourced or offshore, guaranteeing consistent quality and trust.",
      ],
    },
  ];

  return (
    <div id="the-libertus-difference" className="section-container light-bg section-padding">
      {/* Same background texture */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.02,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div className="section-content-center">
        <div className="section-tag dark-bg text-dark font-weight-600 text-uppercase letter-spacing-1"
          style={{ backgroundColor: "rgba(26, 26, 26, 0.08)" }}>
          Why Sovereignty Matters: The Libertus AI Advantage
        </div>

        <h2 className="section-title text-dark font-weight-700">
          Why Sovereignty Beats Convenience: Unlocking True Enterprise Value
        </h2>

        <p className="section-description text-medium-gray line-height-1-5">
          The allure of convenient cloud AI often masks significant hidden costs—foremost among them, the compromise of your data sovereignty and long-term control. At Libertus AI, we demonstrate that true enterprise value comes from a foundation of absolute security, predictable performance, and unwavering compliance. Discover how prioritizing sovereignty not only mitigates risk but also drives superior operational efficiency and a healthier bottom line.
        </p>
      </div>

      <div className="card-grid">
        {platformComponents.map((component, index) => (
          <div
            key={index}
            className={`base-card light-bg hover-scale card-animation-on-load ${isVisible ? 'is-visible' : ''}`}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <div className="card-icon" style={{ marginBottom: "20px" }}>{component.icon}</div>
            <h3 className="card-title text-dark">
              {component.heading}
            </h3>
            <p className="card-description text-medium-gray">
              {component.subheading}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, marginTop: "20px" }}>
              {component.points.map((point, pointIndex) => (
                <li
                  key={pointIndex}
                  className="detail-item" /* Added class for consistent styling */
                >
                  {/* Removed span.detail-bullet-line as it's now handled by CSS pseudo-element */}
                  <span dangerouslySetInnerHTML={{ __html: point.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* This CTA is for the old section, remove it */}
      {/*
      <div className="dark-bg section-padding text-center border-radius-20 margin-bottom-40">
        <h3 className="section-title text-white font-weight-700">
          Professional installation and ongoing support included
        </h3>

        <p className="section-description text-medium-gray margin-bottom-40">
          Our team handles everything from hardware procurement to user
          training. You get a fully operational AI platform with enterprise
          support.
        </p>

        <div className="flex-gap-20 justify-center flex-wrap">
          <button className="btn-cta-primary hover-button">
            Schedule Technical Demo
          </button>

          <button className="btn-cta-secondary hover-button">
            Download Platform Overview
          </button>
        </div>
      </div>
      */}
    </div>
  );
};

export default ArchitectureSection;
